import React, { useEffect, useRef, useState } from "react";

import s from "./ui.module.scss";

// React is 18, but @types/react is pinned at 17, so useId does not typecheck
// even though it exists at runtime. A module counter is enough anyway: the id
// only has to be unique within the document, and every instance takes the next.
let nextId = 0;

/**
 * An explanation, behind an icon.
 *
 * A DISCLOSURE, not a hover tooltip: it opens on click and stays open until
 * dismissed. Hover-only puts the text out of reach on touch, and these are
 * sentences people read rather than glance at.
 *
 * Accessibility follows the disclosure pattern rather than role="tooltip":
 * `aria-expanded` + `aria-controls` on the trigger, and the panel is a sibling
 * screen readers reach normally. role="tooltip" would promise hover/focus
 * semantics this does not have.
 */
const InfoTooltip = ({
  text,
  label = "More information",
  align = "left",
}: {
  text: React.ReactNode;
  /** What the icon announces. Say which field when several share a row. */
  label?: string;
  /** Which edge the panel hangs from — "right" for a right-aligned table cell. */
  align?: "left" | "right";
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelIdRef = useRef<string>();
  if (!panelIdRef.current) panelIdRef.current = `info-panel-${(nextId += 1)}`;
  const panelId = panelIdRef.current;

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    // Capture phase, so the panel closes even when the click lands on
    // something that stops propagation on its way up.
    document.addEventListener("click", onDocClick, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className={s.infoWrap} ref={wrapRef}>
      <button
        type="button"
        className={s.infoTrigger}
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(e) => {
          // These sit inside labels and clickable rows; explaining a field
          // should not also toggle or navigate it.
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <i className="mdi mdi-information-outline" aria-hidden="true" />
      </button>
      {open && (
        <span
          className={[s.infoPanel, align === "right" ? s.infoPanelRight : null]
            .filter(Boolean)
            .join(" ")}
          id={panelId}
          role="note"
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoTooltip;
